import { isAxiosError } from 'axios';
import { Result, err, ok } from 'neverthrow';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import axios from '@utils/axios';

import { StopTypes } from 'types/Appointment';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import captureException from 'utils/captureException';

export type SendRequestApptEmailBody = {
  loadID: number;
  recipients: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string; // Optional because email reply mode doesn't need subject. Backend will handle it.
  emailBody: string;
  emailTemplateId?: number;
  scheduleSend?: Maybe<string>;
  stopType: StopTypes;
  replyInThread?: boolean;
  threadID?: string;
  threadItemID?: string;
  threadReferences?: string;
  inReplyTo?: string[];
  /** Optional: send from this address (alias or shared mailbox). Validated by backend. */
  from?: string;
};

export async function sendRequestApptEmail(
  body: SendRequestApptEmailBody
): Promise<Result<void, ApiError>> {
  try {
    const payload: Record<
      string,
      Undef<string | number | boolean | string[]>
    > = {
      loadID: body.loadID,
      recipients: body.recipients,
      cc: body.cc,
      bcc: body.bcc,
      subject: body.subject,
      emailBody: body.emailBody,
      stopType: body.stopType,
    };

    if (body.scheduleSend !== null && body.scheduleSend !== undefined) {
      payload.scheduleSend = body.scheduleSend;
    }

    if (body.emailTemplateId !== null && body.emailTemplateId !== undefined) {
      payload.emailTemplateId = body.emailTemplateId;
    }

    if (body.replyInThread) {
      payload.replyInThread = body.replyInThread;
      payload.threadID = body.threadID;
      payload.threadItemID = body.threadItemID;

      const hasInReplyTo = body.inReplyTo && body.inReplyTo.length > 0;
      const threadReferencesFromInReplyTo = hasInReplyTo
        ? body.inReplyTo!.join(' ')
        : undefined;
      const threadReferences =
        body.threadReferences || threadReferencesFromInReplyTo;

      payload.threadReferences = threadReferences;
      payload.inReplyTo = body.inReplyTo;
    }

    if (body.from !== undefined && body.from !== '') {
      payload.from = body.from;
    }

    await axios.post(`scheduling/email/request-appt`, payload);

    return ok(undefined);
  } catch (error) {
    captureException(error, { functionName: 'sendRequestApptEmail' });

    if (!isAxiosError(error)) {
      return err({ message: 'Failed to send appointment request email' });
    }

    if (error && error.message === 'Extension context invalidated.') {
      throw error;
    }

    return err({
      message:
        error.response?.data?.message ||
        error.response?.data ||
        'Failed to send appointment request email',
    });
  }
}
